import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X } from 'lucide-react';
import type { BusinessPartner } from '../../types/business';
import { gql, useMutation } from '@apollo/client';

const businessPartnerSchema = z.object({
  name: z.string().min(2, 'Le nom est requis'),
  industry: z.string().min(2, "Le secteur d'activité est requis"),
  size: z.enum(['small', 'medium', 'large']),
  contactPerson: z.object({
    name: z.string().min(2, 'Le nom est requis'),
    position: z.string().min(2, 'Le poste est requis'),
    email: z.string().email('Email invalide'),
    phone: z.string().min(10, 'Numéro de téléphone invalide'),
  }),
  address: z.object({
    street: z.string().min(2, 'La rue est requise'),
    city: z.string().min(2, 'La ville est requise'),
    country: z.string().min(2, 'Le pays est requis'),
  }),
  logo: z.string().url().optional(),
});

type BusinessPartnerFormData = z.infer<typeof businessPartnerSchema>;

interface Business {
  id: number;
  companyName: string;
  sector: string;
  logo: string | null;
  contactName: string;
  contactLastName: string;
  contactPhone: string;
  contactPosition: string;
  address: string;
  city: string;
  country: string;
  email: string;
  website?: string;
  description?: string;
  isActive: boolean;
}

interface BusinessFormData {
  companyName: string;
  sector: string;
  contactName: string;
  contactLastName: string;
  contactPhone: string;
  email: string;
  contactPosition: string;
  address: string;
  city: string;
  country: string;
  website?: string;
  description?: string;
  logo?: string | null;
}

interface BusinessPartnerFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Business) => void;
  selectedBusiness: Business | null;
  title: string;
}

const CREATE_BUSINESS = gql`
  mutation CreateBusiness($input: BusinessInput!) {
    createBusiness(input: $input) {
      id
      companyName
      sector
      logo
      contactName
      contactLastName
      contactPhone
      contactPosition
      address
      city
      country
      email
      website
      description
      isActive
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_BUSINESS = gql`
  mutation UpdateBusiness($id: Int!, $input: BusinessInput!) {
    updateBusiness(id: $id, input: $input) {
      id
      companyName
      sector
      logo
      contactName
      contactLastName
      contactPhone
      contactPosition
      address
      city
      country
      email
      website
      description
      isActive
      createdAt
      updatedAt
    }
  }
`;

// Ajouter la fonction pour convertir l'image en base64
const convertToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

export default function BusinessPartnerFormModal({
  isOpen,
  onClose,
  onSubmit,
  selectedBusiness,
  title,
}: BusinessPartnerFormModalProps) {
  console.log('BusinessPartnerFormModal - Props reçues:', { 
    isOpen, 
    selectedBusiness,
    title 
  });

  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const { register, handleSubmit, reset, formState: { errors } } = useForm<BusinessFormData>({
    defaultValues: selectedBusiness || {
      companyName: '',
      sector: '',
      contactName: '',
      contactLastName: '',
      contactPhone: '',
      email: '',
      contactPosition: '',
      address: '',
      city: '',
      country: '',
      website: '',
      description: '',
      logo: null
    }
  });

  // Effet pour pré-remplir le formulaire lors de la modification
  useEffect(() => {
    if (selectedBusiness && isOpen) {
      reset({
        companyName: selectedBusiness.companyName,
        sector: selectedBusiness.sector,
        contactName: selectedBusiness.contactName,
        contactLastName: selectedBusiness.contactLastName,
        contactPhone: selectedBusiness.contactPhone,
        email: selectedBusiness.email,
        contactPosition: selectedBusiness.contactPosition,
        address: selectedBusiness.address,
        city: selectedBusiness.city,
        country: selectedBusiness.country,
        website: selectedBusiness.website || '',
        description: selectedBusiness.description || '',
        logo: selectedBusiness.logo
      });

      if (selectedBusiness.logo) {
        setLogoPreview(selectedBusiness.logo);
      }
    }
  }, [selectedBusiness, isOpen, reset]);

  // Effet pour réinitialiser le formulaire quand le modal se ferme
  useEffect(() => {
    if (!isOpen) {
      reset();
      setLogoPreview(null);
    }
  }, [isOpen, reset]);

  // Ajouter un effet pour logger les changements d'état
  useEffect(() => {
    console.log('État du modal changé:', { isOpen, selectedBusiness });
  }, [isOpen, selectedBusiness]);

  const [createBusiness, { loading: createLoading }] = useMutation(CREATE_BUSINESS);
  const [updateBusiness, { loading: updateLoading }] = useMutation(UPDATE_BUSINESS);

  // Gérer le changement de logo
  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const base64 = await convertToBase64(file);
        setLogoPreview(base64);
        setLogoFile(file);
      } catch (error) {
        console.error('Erreur lors de la conversion du logo:', error);
      }
    }
  };

  const handleFormSubmit = async (data: BusinessFormData) => {
    try {
      let logoBase64 = selectedBusiness?.logo || null;

      if (logoFile) {
        logoBase64 = await convertToBase64(logoFile);
      }

      const formData = {
        ...data,
        logo: logoBase64
      };


      if (selectedBusiness) {
        const result = await updateBusiness({
          variables: {
            id: selectedBusiness.id,
            input: formData
          }
        });
        onSubmit(result.data.updateBusiness);
      } else {
        const createData = {
          ...formData,
          agencyName: user.agencyName
        };
        const result = await createBusiness({
          variables: {
            input: createData
          }
        });
        onSubmit(result.data.createBusiness);
      }
      onClose();
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
    }
  };

  if (!isOpen) {
    console.log('Modal fermé, pas de rendu');
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-8">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-8">
            {/* Section Informations de l'entreprise */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 pb-2 border-b">
                Informations de l'entreprise
              </h3>
              
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom de l'entreprise <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register('companyName')}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Secteur d'activité <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register('sector')}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300"
                />
                {logoPreview && (
                  <div className="mt-2">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="h-20 w-20 object-contain rounded-lg border border-gray-200"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Section Contact Principal */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 pb-2 border-b">
                Contact Principal
              </h3>
              
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prénom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register('contactName')}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register('contactLastName')}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    {...register('contactPhone')}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    {...register('email')}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Poste <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register('contactPosition')}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Section Adresse */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 pb-2 border-b">
                Adresse
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rue <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('address')}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ville <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register('city')}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pays <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register('country')}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Boutons */}
            <div className="flex justify-end space-x-4 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-gray-200 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={createLoading || updateLoading}
                className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50"
              >
                {createLoading || updateLoading ? 'Chargement...' : selectedBusiness ? 'Modifier' : 'Ajouter'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}